<div class="container-fluid mat-elevation-z8 detail-page">
  <div class="loading-shade" *ngIf="isLoading || hasError">
    <mat-spinner *ngIf="isLoading"></mat-spinner>
    <div class="has-error" *ngIf="hasError">
      There was an error loading this user's message flags.
    </div>
  </div>

  <div class="w-100 p-3" *ngIf="detail">
    <button mat-stroked-button class="mb-3" (click)="backToList()">
      <mat-icon>arrow_back</mat-icon> Back to Message Flags
    </button>

    <div class="d-flex flex-wrap align-items-center header-row">
      <img class="profile-image me-3" *ngIf="profileImage()" [src]="profileImage()"/>
      <div class="me-4">
        <h2 class="m-0">{{ displayName() }}</h2>
        <div class="subtle">#{{ detail.user.user_id }} · {{ detail.user.email }}</div>
      </div>
      <button mat-stroked-button color="primary" (click)="openUser()">Open user profile</button>
    </div>

    <!-- Automated flags on the left, what other users did on the right. -->
    <div class="summary-grid mt-3">
      <div class="summary-card">
        <div class="summary-value">{{ detail.summary.flag_count }}</div>
        <div class="summary-label">Flags</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ detail.summary.flagged_message_count }}</div>
        <div class="summary-label">Flagged messages</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ detail.summary.distinct_recipients }}</div>
        <div class="summary-label">Recipients</div>
      </div>
      <div class="summary-card" [class.alert]="detail.summary.report_count > 0">
        <div class="summary-value">{{ detail.summary.report_count }}</div>
        <div class="summary-label">Reports</div>
      </div>
      <div class="summary-card" [class.alert]="detail.summary.block_count > 0">
        <div class="summary-value">{{ detail.summary.block_count }}</div>
        <div class="summary-label">Blocks</div>
      </div>
    </div>

    <h3 class="section-title">Why this account is flagged</h3>
    <div class="reason-filters">
      <button mat-stroked-button class="me-2 mb-2"
              [color]="activeReason === '' ? 'primary' : null"
              (click)="reasonFilterChanged('')">
        All ({{ detail.summary.flag_count }})
      </button>
      <button mat-stroked-button class="me-2 mb-2" *ngFor="let reason of detail.summary.reasons"
              [color]="activeReason === reason.reason_code ? 'primary' : null"
              (click)="reasonFilterChanged(reason.reason_code)">
        {{ reason.label }} ({{ reason.count }})
      </button>
    </div>

    <h3 class="section-title">Flagged messages ({{ messages.length }})</h3>
    <div class="messages">
      <div class="message-card" *ngFor="let message of messages"
           [class.deleted]="message.deletedAt">
        <div class="message-meta">
          <span class="to">To {{ recipientLabel(message) }}</span>
          <span class="sep">·</span>
          <span>{{ message.createdAt | timezoneDate }} ({{ timezoneAbbr }})</span>
          <span class="sep" *ngIf="message.deletedAt">·</span>
          <span class="deleted-tag" *ngIf="message.deletedAt">deleted</span>
        </div>

        <!-- Message body with the matched substring highlighted in place. -->
        <div class="message-text">
          <span *ngFor="let part of highlightParts(message.text, message.reasons)"
                [class.match]="part.match">{{ part.text }}</span>
        </div>

        <div class="message-reasons">
          <div class="reason-line" *ngFor="let reason of message.reasons">
            <span class="reason-chip">{{ reason.label }}</span>
            <span class="matched-text" *ngIf="reason.matched_text">matched: “{{ reason.matched_text }}”</span>
          </div>
        </div>
      </div>

      <div class="empty-state" *ngIf="messages.length === 0">
        No flagged messages for this filter.
      </div>

      <div class="empty-state" *ngIf="detail.messages_truncated">
        Showing the most recent flags only — this account has more history than
        the page displays.
      </div>
    </div>

    <h3 class="section-title">Reports filed against this user ({{ detail.reports.length }})</h3>
    <div class="reports">
      <div class="report-card" *ngFor="let report of detail.reports">
        <div class="report-body">
          <!-- Who reported, shown the same way as a blocker. -->
          <img class="reporter-photo" *ngIf="report.reporter?.profile_image"
               [src]="report.reporter.profile_image" [alt]="reporterName(report)"/>
          <div class="reporter-photo reporter-photo-empty"
               *ngIf="report.reporting_user_id && !report.reporter?.profile_image">
            <mat-icon>person</mat-icon>
          </div>

          <div class="report-main">
            <div class="report-meta">
              <span class="reporter-name" *ngIf="report.reporting_user_id">{{ reporterName(report)
                }}<span class="reporter-age" *ngIf="report.reporter?.age">, {{ report.reporter.age }}</span>
                <span class="reporter-id">#{{ report.reporting_user_id }}</span>
              </span>
              <span class="sep" *ngIf="report.reporting_user_id">·</span>
              <span>{{ report.createdAt | timezoneDate }} ({{ timezoneAbbr }})</span>
              <span class="sep" *ngIf="report.from_carliminator">·</span>
              <span class="auto-tag" *ngIf="report.from_carliminator">automated</span>
            </div>
            <div class="report-reason">
              <b>{{ report.reason_code || report.reason || 'unspecified' }}</b>
            </div>
            <div class="report-text" *ngIf="report.reason_text">{{ report.reason_text }}</div>
          </div>
        </div>
      </div>

      <div class="empty-state" *ngIf="detail.reports.length === 0">
        Nobody has reported this user.
      </div>
    </div>

    <h3 class="section-title">Blocks ({{ detail.blocks.length }})</h3>
    <div class="blocks">
      <div class="blocker-card" *ngFor="let block of detail.blocks">
        <img class="blocker-photo" *ngIf="block.blocker?.profile_image"
             [src]="block.blocker.profile_image" [alt]="blockerName(block)"/>
        <div class="blocker-photo blocker-photo-empty" *ngIf="!block.blocker?.profile_image">
          <mat-icon>person</mat-icon>
        </div>
        <div class="blocker-meta">
          <div class="blocker-name">{{ blockerName(block)
            }}<span class="blocker-age" *ngIf="block.blocker?.age">, {{ block.blocker.age }}</span></div>
          <div class="blocker-sub">#{{ block.user_id }} · {{ block.createdAt | timezoneDate }}</div>
        </div>
      </div>
      <div class="empty-state" *ngIf="detail.blocks.length === 0">
        Nobody has blocked this user.
      </div>
    </div>
  </div>
</div>
